import { SimpleGrid, Tabs, Skeleton } from '@mantine/core'
import {
  IconChartBar,
  IconChartLine,
  IconCoin,
  IconServer,
  IconArrowDown,
  IconPigMoney,
  IconTrash,
} from '@tabler/icons-react'
import { useSearchParams } from 'react-router-dom'
import { IconList } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { finopsApi } from '@/api/finops'
import { formatUSD } from '@/utils/finops'
import { SmallBox } from '@/components/ui'
import { RecommendationsTab } from './recomendacoes/RecommendationsTab'
import { RightsizingTab } from './recomendacoes/RightsizingTab'
import { SavingsPlansTab } from './recomendacoes/SavingsPlansTab'
import { ShowbackTab } from './recomendacoes/ShowbackTab'
import { ForecastTab } from './recomendacoes/ForecastTab'

const VALID_TABS = ['all', 'rightsizing', 'savings', 'orphans', 'showback', 'forecast'] as const
type TabValue = (typeof VALID_TABS)[number]

function isTabValue(v: string | null): v is TabValue {
  return v != null && (VALID_TABS as readonly string[]).includes(v)
}

export function Recomendacoes() {
  const savingsQuery = useQuery({
    queryKey: ['finops-recommendations-savings'],
    queryFn: () => finopsApi.getRecommendationSavings(),
    staleTime: 30_000,
  })
  const isLoading = savingsQuery.isLoading
  const estimated = savingsQuery.data?.estimated_monthly_savings_usd ?? 0
  const realized = savingsQuery.data?.realized_monthly_savings_usd ?? 0
  const pending = savingsQuery.data?.pending_count ?? 0

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: TabValue = isTabValue(tabParam) ? tabParam : 'all'

  const onTabChange = (value: string | null) => {
    const next = isTabValue(value) ? value : 'all'
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', next)
      return params
    })
  }

  return (
    <>
      {/* KPI bar */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="xl">
        {isLoading ? (
          <>
            <Skeleton height={100} />
            <Skeleton height={100} />
            <Skeleton height={100} />
          </>
        ) : (
          <>
            <SmallBox
              label="Economia Potencial Total"
              value={`${formatUSD(estimated)}/mês`}
              icon={<IconPigMoney size={40} />}
              color="success"
              info="Soma da economia mensal estimada das recomendações ativas (todos os tenants do seu escopo)."
            />
            <SmallBox
              label="Economia Realizada"
              value={`${formatUSD(realized)}/mês`}
              icon={<IconCoin size={40} />}
              color="info"
              info="Economia mensal já verificada após a execução de recomendações."
            />
            <SmallBox
              label="Pendentes de Ação"
              value={String(pending)}
              icon={<IconArrowDown size={40} />}
              color="warning"
              info="Recomendações recomendadas/aprovadas aguardando execução."
            />
          </>
        )}
      </SimpleGrid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={onTabChange} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="all" leftSection={<IconList size={16} />}>
            Todas
          </Tabs.Tab>
          <Tabs.Tab value="rightsizing" leftSection={<IconServer size={16} />}>
            Rightsizing EC2
          </Tabs.Tab>
          <Tabs.Tab value="savings" leftSection={<IconCoin size={16} />}>
            Savings Plans
          </Tabs.Tab>
          <Tabs.Tab value="orphans" leftSection={<IconTrash size={16} />}>
            Recursos Ociosos
          </Tabs.Tab>
          <Tabs.Tab value="showback" leftSection={<IconChartBar size={16} />}>
            Showback
          </Tabs.Tab>
          <Tabs.Tab value="forecast" leftSection={<IconChartLine size={16} />}>
            Forecast
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all" pt="md">
          <RecommendationsTab />
        </Tabs.Panel>
        <Tabs.Panel value="rightsizing" pt="md">
          <RightsizingTab />
        </Tabs.Panel>
        <Tabs.Panel value="savings" pt="md">
          <SavingsPlansTab />
        </Tabs.Panel>
        <Tabs.Panel value="orphans" pt="md">
          <RecommendationsTab
            recTypes={[
              'idle_eip',
              'idle_load_balancer',
              'idle_nat_gateway',
              'rds_idle',
              'orphan_ebs',
              'old_snapshot',
              'old_ami',
              'gp2_to_gp3',
              's3_lifecycle',
            ]}
          />
        </Tabs.Panel>
        <Tabs.Panel value="showback" pt="md">
          <ShowbackTab />
        </Tabs.Panel>
        <Tabs.Panel value="forecast" pt="md">
          <ForecastTab />
        </Tabs.Panel>
      </Tabs>
    </>
  )
}
